import React from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import Login from './Login';

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background);
  gap: var(--space-md);
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${props => props.theme.colors.border};
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSize.md};
  margin: 0;
`;

const UnauthorizedContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: var(--color-background);
  gap: var(--space-lg);
  padding: var(--space-xl);
  text-align: center;
`;

const UnauthorizedTitle = styled.h1`
  color: ${props => props.theme.colors.error};
  font-family: var(--font-serif);
  font-size: ${props => props.theme.fontSize['2xl']};
  margin: 0;
`;

const UnauthorizedMessage = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${props => props.theme.fontSize.lg};
  margin: 0;
  max-width: 500px;
`;

const LogoutButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: var(--space-sm) var(--space-lg);
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.md};
  cursor: pointer;
  transition: background-color var(--transition-speed);

  &:hover {
    background-color: ${props => props.theme.colors.primaryDark};
  }
`;

const ProtectedRoute = ({ children }) => {
  const { user, loading, isAdmin, adminLoading, logout } = useAuth();

  // Show loading during initial authentication check
  if (loading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Authenticating...</LoadingText>
      </LoadingContainer>
    );
  }

  // Show login if not authenticated
  if (!user) {
    return <Login />;
  }

  // Show loading during admin verification
  if (adminLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Verifying admin access...</LoadingText>
      </LoadingContainer>
    );
  }

  // Show unauthorized access if authenticated but not admin
  if (!isAdmin) {
    return (
      <UnauthorizedContainer>
        <UnauthorizedTitle>Unauthorized Access</UnauthorizedTitle>
        <UnauthorizedMessage>
          Your account does not have administrator privileges required to access this application.
          Please contact your system administrator if you believe this is an error.
        </UnauthorizedMessage>
        <LogoutButton onClick={logout}>
          Sign Out
        </LogoutButton>
      </UnauthorizedContainer>
    );
  }

  // User is authenticated and is an admin
  return children;
};

export default ProtectedRoute;
